import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import {
  updatePassword,
  updateEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";

function Profile() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    experience: "",
    students: "", // Numri i kandidatet
    reliability: "", // Kalueshmeria
    numberOfInstructors: "", // Numri i instruktoreve
    location: "", // Lokacioni
    description: "", // Description
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Fetch user data and school data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          // Fetch user data
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          let userDataObj = null;
          if (userDoc.exists()) {
            userDataObj = userDoc.data();
            setUserData(userDataObj);
            const formDataObj = {
              firstName: userDataObj.firstName || "",
              lastName: userDataObj.lastName || "",
              phoneNumber: userDataObj.phoneNumber || "",
              email: currentUser.email || "",
              experience: userDataObj.experience || "",
              students: userDataObj.students || "",
              reliability: userDataObj.reliability || "",
              numberOfInstructors: userDataObj.numberOfInstructors || "",
              location: userDataObj.location || "",
              description: userDataObj.description || "",
            };
            setFormData(formDataObj);
          }

          // Fetch school data (from a school document or use user data if admin)
          try {
            const schoolDoc = await getDoc(doc(db, "school", "info"));
            if (schoolDoc.exists()) {
              const schoolInfo = schoolDoc.data();
              // Update formData and userData with school data if user is admin
              if (userDataObj && userDataObj.role === "admin") {
                const mergedData = {
                  experience:
                    schoolInfo.experience || userDataObj.experience || "",
                  students: schoolInfo.students || userDataObj.students || "",
                  reliability:
                    schoolInfo.reliability || userDataObj.reliability || "",
                  numberOfInstructors:
                    schoolInfo.numberOfInstructors ||
                    userDataObj.numberOfInstructors ||
                    "",
                  location: schoolInfo.location || userDataObj.location || "",
                  email: schoolInfo.email || userDataObj.email || "",
                  description:
                    schoolInfo.description || userDataObj.description || "",
                };
                setFormData((prev) => ({
                  ...prev,
                  ...mergedData,
                }));
                // Also update userData so it displays correctly in view mode
                setUserData((prev) => ({
                  ...prev,
                  ...mergedData,
                }));
              }
            }
          } catch (schoolError) {
            console.error("Error fetching school data:", schoolError);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          if (
            error.code === "permission-denied" ||
            error.code === "missing-or-insufficient-permissions"
          ) {
            showToast(
              "Permission denied. Please make sure your user document exists in Firestore. Create admin via /setup-admin",
              "error"
            );
          } else {
            showToast(t("profile.toast.fetchError"), "error");
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [t]);

  // Show toast notification
  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle blur
  const handleBlur = (name) => {
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  // Validate profile form
  const validateProfileForm = () => {
    const newErrors = {};

    if (!formData.firstName || formData.firstName.trim() === "") {
      newErrors.firstName = t("profile.errors.firstNameRequired");
    }

    if (!formData.lastName || formData.lastName.trim() === "") {
      newErrors.lastName = t("profile.errors.lastNameRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate password form
  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = t("profile.errors.currentPasswordRequired");
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = t("profile.errors.newPasswordRequired");
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = t("profile.errors.passwordMinLength");
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = t("profile.errors.confirmPasswordRequired");
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t("profile.errors.passwordsDoNotMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle profile update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    if (!validateProfileForm()) {
      return;
    }

    try {
      const userRef = doc(db, "users", user.uid);
      const updateData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phoneNumber: formData.phoneNumber.trim() || null,
      };

      // Add description field for all users (candidates, instructors, admins)
      updateData.description = formData.description.trim() || null;

      // Add school fields if user is admin
      if (userData?.role === "admin") {
        updateData.experience = formData.experience.trim() || null;
        updateData.students = formData.students.trim() || null;
        updateData.reliability = formData.reliability.trim() || null;
        updateData.numberOfInstructors =
          formData.numberOfInstructors.trim() || null;
        updateData.location = formData.location.trim() || null;
        updateData.email = formData.email.trim() || null;
      }

      await updateDoc(userRef, updateData);

      // Also update school document if it exists
      if (userData?.role === "admin") {
        try {
          const schoolRef = doc(db, "school", "info");
          try {
            await updateDoc(schoolRef, {
              experience: formData.experience.trim() || null,
              students: formData.students.trim() || null,
              reliability: formData.reliability.trim() || null,
              numberOfInstructors: formData.numberOfInstructors.trim() || null,
              location: formData.location.trim() || null,
              email: formData.email.trim() || null,
              description: formData.description.trim() || null,
            });
          } catch (updateError) {
            // If school document doesn't exist, create it
            if (updateError.code === "not-found") {
              await setDoc(schoolRef, {
                experience: formData.experience.trim() || null,
                students: formData.students.trim() || null,
                reliability: formData.reliability.trim() || null,
                numberOfInstructors:
                  formData.numberOfInstructors.trim() || null,
                location: formData.location.trim() || null,
                email: formData.email.trim() || null,
                description: formData.description.trim() || null,
              });
            } else {
              throw updateError;
            }
          }
        } catch (schoolError) {
          console.error("Error updating school data:", schoolError);
        }
      }

      // Update local state
      setUserData((prev) => ({
        ...prev,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phoneNumber: formData.phoneNumber.trim() || null,
        email: formData.email.trim() || prev.email,
        experience: formData.experience.trim() || null,
        students: formData.students.trim() || null,
        reliability: formData.reliability.trim() || null,
        numberOfInstructors: formData.numberOfInstructors.trim() || null,
        location: formData.location.trim() || null,
        description: formData.description.trim() || null,
      }));

      showToast(t("profile.toast.updated"), "success");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      if (error.code === "auth/requires-recent-login") {
        showToast(t("profile.toast.reauthRequired"), "error");
      } else {
        showToast(t("profile.toast.updateError"), "error");
      }
    }
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    try {
      // Reauthenticate user
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, passwordData.newPassword);

      // Reset form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
      setTouched({});
      setIsChangingPassword(false);

      showToast(t("profile.toast.passwordChanged"), "success");
    } catch (error) {
      console.error("Error changing password:", error);
      if (error.code === "auth/wrong-password") {
        setErrors((prev) => ({
          ...prev,
          currentPassword: t("profile.errors.incorrectPassword"),
        }));
      } else if (error.code === "auth/weak-password") {
        setErrors((prev) => ({
          ...prev,
          newPassword: t("profile.errors.passwordMinLength"),
        }));
      } else {
        showToast(t("profile.toast.passwordChangeError"), "error");
      }
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      firstName: userData?.firstName || "",
      lastName: userData?.lastName || "",
      phoneNumber: userData?.phoneNumber || "",
      email: user?.email || "",
      experience: userData?.experience || "",
      students: userData?.students || "",
      reliability: userData?.reliability || "",
      numberOfInstructors: userData?.numberOfInstructors || "",
      location: userData?.location || "",
      description: userData?.description || "",
    });
    setErrors({});
    setTouched({});
  };

  // Cancel password change
  const handleCancelPassword = () => {
    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({});
    setTouched({});
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">{t("profile.loading")}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!user || !userData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-gray-600">{t("profile.noUserData")}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast.show && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            <span>{toast.message}</span>
            <button
              onClick={() => setToast({ show: false, message: "", type: "" })}
              className="ml-4 hover:opacity-75"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Page Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t("profile.title")}
          </h2>
          <p className="text-gray-600">{t("profile.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("profile.personalInfo")}
                </h3>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                  >
                    {t("profile.edit")}
                  </button>
                )}
              </div>

              {!isEditing ? (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("profile.firstName")}
                      </label>
                      <p className="text-sm text-gray-900">
                        {userData.firstName || "-"}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("profile.lastName")}
                      </label>
                      <p className="text-sm text-gray-900">
                        {userData.lastName || "-"}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("profile.email")}
                      </label>
                      <p className="text-sm text-gray-900">
                        {user.email || "-"}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("profile.phoneNumber")}
                      </label>
                      <p className="text-sm text-gray-900">
                        {userData.phoneNumber || "-"}
                      </p>
                    </div>

                    {userData?.role === "admin" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            {t("profile.experience")}
                          </label>
                          <p className="text-sm text-gray-900">
                            {userData.experience || "-"}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            {t("profile.students")}
                          </label>
                          <p className="text-sm text-gray-900">
                            {userData.students || "-"}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            {t("profile.numberOfInstructors")}
                          </label>
                          <p className="text-sm text-gray-900">
                            {userData.numberOfInstructors || "-"}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            {t("profile.location")}
                          </label>
                          <p className="text-sm text-gray-900">
                            {userData.location || "-"}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            {t("profile.reliability")}
                          </label>
                          <p className="text-sm text-gray-900">
                            {userData.reliability || "-"}
                          </p>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("profile.role")}
                      </label>
                      <p className="text-sm text-gray-900 capitalize">
                        {userData.role || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      {userData?.role === "candidate"
                        ? t("profile.schoolRating")
                        : t("profile.description")}
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap min-h-[60px]">
                      {userData.description || "-"}
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleUpdateProfile} className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("profile.firstName")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        onBlur={() => handleBlur("firstName")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.firstName && errors.firstName
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.firstName && errors.firstName && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("profile.lastName")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        onBlur={() => handleBlur("lastName")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.lastName && errors.lastName
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.lastName && errors.lastName && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.lastName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("profile.email")}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("profile.phoneNumber")}
                      </label>
                      <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        onBlur={() => handleBlur("phoneNumber")}
                        placeholder="+383 XX XXX XXX"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                      />
                    </div>

                    {userData?.role === "admin" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("profile.experience")}
                          </label>
                          <input
                            type="text"
                            name="experience"
                            value={formData.experience}
                            onChange={handleChange}
                            placeholder={t("profile.experiencePlaceholder")}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("profile.students")}
                          </label>
                          <input
                            type="text"
                            name="students"
                            value={formData.students}
                            onChange={handleChange}
                            placeholder={t("profile.studentsPlaceholder")}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("profile.numberOfInstructors")}
                          </label>
                          <input
                            type="text"
                            name="numberOfInstructors"
                            value={formData.numberOfInstructors}
                            onChange={handleChange}
                            placeholder={t(
                              "profile.numberOfInstructorsPlaceholder"
                            )}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("profile.location")}
                          </label>
                          <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder={t("profile.locationPlaceholder")}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t("profile.reliability")}
                          </label>
                          <input
                            type="text"
                            name="reliability"
                            value={formData.reliability}
                            onChange={handleChange}
                            placeholder={t("profile.reliabilityPlaceholder")}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("profile.role")}
                      </label>
                      <input
                        type="text"
                        value={userData?.role || "-"}
                        disabled
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed capitalize"
                      />
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {userData?.role === "candidate"
                        ? t("profile.schoolRating")
                        : t("profile.description")}
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={5}
                      placeholder={
                        userData?.role === "candidate"
                          ? t("profile.schoolRatingPlaceholder")
                          : t("profile.descriptionPlaceholder")
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 resize-y"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 mt-4">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      {t("common.cancel")}
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                    >
                      {t("profile.save")}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Password Change Card - Only show for non-candidates */}
          {userData?.role !== "candidate" && (
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("profile.password")}
                  </h3>
                </div>

                {!isChangingPassword ? (
                  <div className="p-6">
                    <p className="text-sm text-gray-600 mb-4">
                      {t("profile.passwordDescription")}
                    </p>
                    <button
                      onClick={() => setIsChangingPassword(true)}
                      className="w-full px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                      {t("profile.changePassword")}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleChangePassword} className="p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("profile.currentPassword")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          onBlur={() => handleBlur("currentPassword")}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.currentPassword && errors.currentPassword
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.currentPassword && errors.currentPassword && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.currentPassword}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("profile.newPassword")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          onBlur={() => handleBlur("newPassword")}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.newPassword && errors.newPassword
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.newPassword && errors.newPassword && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.newPassword}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("profile.confirmPassword")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="password"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          onBlur={() => handleBlur("confirmPassword")}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                            touched.confirmPassword && errors.confirmPassword
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {touched.confirmPassword && errors.confirmPassword && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={handleCancelPassword}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                        >
                          {t("common.cancel")}
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                        >
                          {t("profile.updatePassword")}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Profile;
